import { useMemo } from "react";
import { motion } from "framer-motion";
import { seeded } from "../../lib/motion";
import { usePrefersReducedMotion } from "../../hooks/useBreakpoint";

const MAX_CHIPS = 60;

/**
 * The live pool of tickets in a raffle. Chips show real ticket numbers; the
 * collector's own tickets glow. Used both as ambience while entries are open
 * and as the shuffle bed before the reel spins.
 */
export function TicketWall({
  ticketNos,
  myTicketNos = [],
  shuffling = false,
  className = "",
}: {
  ticketNos: number[];
  myTicketNos?: number[];
  shuffling?: boolean;
  className?: string;
}) {
  const reduced = usePrefersReducedMotion();
  const ticketCount = ticketNos.length;
  const shownNos = ticketNos.slice(0, MAX_CHIPS);
  const mine = useMemo(() => new Set(myTicketNos), [myTicketNos]);

  if (ticketCount <= 0) {
    return (
      <p className={`text-center text-[11px] text-white/35 ${className}`}>
        No tickets in the pool yet — be the first.
      </p>
    );
  }

  return (
    <div className={className}>
      <div className="flex flex-wrap justify-center gap-1.5">
        {shownNos.map((no, i) => {
          const isMine = mine.has(no);
          return (
            <motion.span
              key={no}
              className={`rounded-md px-1.5 py-1 font-display text-[10px] font-bold tabular-nums ${
                isMine
                  ? "bg-gradient-brand text-white shadow-md shadow-brand-pink/30"
                  : "bg-white/[0.06] text-white/45"
              }`}
              animate={
                shuffling && !reduced
                  ? { opacity: [0.35, 1, 0.35], scale: [1, 1.14, 1] }
                  : { opacity: 1, scale: 1 }
              }
              transition={
                shuffling && !reduced
                  ? { duration: 0.5 + seeded(i, 3) * 0.5, repeat: Infinity, delay: seeded(i, 9) * 0.6 }
                  : { duration: 0.2 }
              }
            >
              #{no}
            </motion.span>
          );
        })}
      </div>
      {ticketCount > shownNos.length && (
        <p className="mt-2 text-center text-[10px] text-white/35">
          + {ticketCount - shownNos.length} more in the pool
        </p>
      )}
    </div>
  );
}
